
// import { useStore } from '@/store'
import {
    computed,
    defineComponent,
    Emitter,
    inject,
    onBeforeUnmount,
    onMounted,
    PropType,
    ref,
    watch
} from 'vue'
import { useI18n } from 'vue-i18n'
import { RoadMapData } from './datamodel/RoadMapData'
import { DISPLAY, RoadType } from './datamodel/types'
import {
    drawRoadMap,
    drawRoadMapGrid,
    drawRoadMapGrid2,
    drawRoadMapGrid3,
    drawRoadMapGrid4,
    drawSingleGridMap,
    parseRoadMap
} from './utils'
import { RoadMapProperties } from './datamodel/RoadMapProperties'
import { useStore } from '@/store'
import { EventBuss } from '@/types/global'

export default defineComponent({
    name: 'road-map',
    emits: ['road-map-data', 'road-result-preview'],
    props: {
        enableClicked: {
            default: false,
            required: false,
            type: Boolean as PropType<boolean>
        },
        mapData: {
            required: false,
            type: String as PropType<string>
        },
        gameType: {
            default: () => 1,
            type: Number
        },
        enablePrediction: {
            default: () => false,
            type: Boolean
        },
        beadRowCount: {
            default: () => 13,
            type: Number
        },
        display: {
            default: () => 0,
            type: Number
        },
        isTransparent: {
            type: Boolean as PropType<boolean>,
            required: false,
            default: () => false
        },
        borderColor: {
            type: String as PropType<string>,
            default: () => ''
        },
        lineWidth: {
            default: () => 0,
            type: Number as PropType<number>
        }
    },
    setup(props, context) {
        const emitter = inject('emitter') as Emitter
        const { t } = useI18n()
        const store = useStore()
        const readyToRender = ref<boolean>(false)
        const mapCanvas = ref()
        const roadMapData = ref<RoadMapData>(new RoadMapData())
        const nextRed = ref<RoadMapData>(new RoadMapData())
        const nextBlue = ref<RoadMapData>(new RoadMapData())
        const predictionTimer = ref<number | boolean | any>(false)
        const predictionType = ref<number>(0)
        const predictionCount = ref<number>(9)
        const predictionBlink = ref<number>(9)

        let beadsArray: { index: number; path2D: Path2D }[] | undefined

        const drawMap = () => {
            beadsArray = []
            if (!readyToRender.value) return

            const display = props.display

            if (display === DISPLAY.default_horizontal) {
                drawDefaultHorizontalMap(roadMapData.value)
            } else if (display === DISPLAY.default_vertical) {
                drawDefaultVerticalMap(roadMapData.value)
            } else if (display === DISPLAY.big) {
                drawOnlyBigMap(roadMapData.value)
            } else if (display === DISPLAY.no_bead_vertical) {
                drawNoBeadVertical(roadMapData.value)
            } else if (display === DISPLAY.no_bead_horizontal) {
                //
            } else if (display === DISPLAY.bead_only) {
                drawBeadOnly(roadMapData.value)
            } else if (display === DISPLAY.default_horizontal_v2) {
                drawDefaultHorizontalMapV2(roadMapData.value)
            }
        }

        const drawDefaultHorizontalMap = (
            mapData: RoadMapData | undefined,
            isPre = false
        ) => {
            const canvas = mapCanvas.value as HTMLCanvasElement

            let borderColor = '#c0c0c0'
            if (props.borderColor !== '') borderColor = props.borderColor

            const prop = drawRoadMapGrid(canvas, props.beadRowCount, false, {
                stroke: borderColor
            })

            if (prop && mapData) {
                prop.isPre = isPre
                const { beads, bigs, bigeyes, smalls, cockroachs } = mapData
                const { width, dpi } = prop

                prop.lineWidth = 1 * dpi
                prop.colWidth = prop.height / 6
                prop.colNum = props.beadRowCount
                beadsArray = drawRoadMap(
                    prop,
                    beads,
                    RoadType.BEAD,
                    0,
                    0,
                    props.gameType,
                    t
                )

                prop.lineWidth = 1.5 * dpi
                prop.colNum = Math.floor(
                    (width - props.beadRowCount * prop.colWidth) /
                        (prop.colWidth / 2)
                )

                // this will serve as x offset of big big,bigeys, small roadmap
                const offsetX1 = prop.colWidth * props.beadRowCount
                drawRoadMap(prop, bigs, RoadType.BIG, 0, offsetX1)
                const offsetY1 = prop.colWidth * 3

                prop.colNum = prop.colNum * 2
                drawRoadMap(prop, bigeyes, RoadType.BIGEYE, offsetY1, offsetX1)

                const offsetY2 = prop.colWidth * 4.5

                prop.colNum = Math.floor(prop.colNum / 2)
                drawRoadMap(prop, smalls, RoadType.SMALL, offsetY2, offsetX1)
                const offsetX2 = offsetX1 + (prop.colNum * prop.colWidth) / 4
                drawRoadMap(
                    prop,
                    cockroachs,
                    RoadType.COCKROACH,
                    offsetY2,
                    offsetX2
                )
            }
        }

        const drawDefaultHorizontalMapV2 = (
            mapData: RoadMapData | undefined,
            isPre = false
        ) => {
            const canvas = mapCanvas.value as HTMLCanvasElement
            const prop = drawRoadMapGrid4(canvas, true)
        }

        const drawDefaultVerticalMap = (data: RoadMapData, isPre = false) => {
            const canvas = mapCanvas.value as HTMLCanvasElement
            const prop = drawRoadMapGrid2(canvas)

            if (prop && data) {
                prop.isPre = isPre
                const { beads, bigs, bigeyes, smalls, cockroachs } = data
                const { width, colWidth } = prop

                prop.colNum = Math.floor(width / colWidth)
                drawRoadMap(prop, beads, RoadType.BEAD, 0, 0, props.gameType, t)

                const offsetY1 = colWidth * 6
                const offsetY2 = offsetY1 + colWidth * 3
                const offsetY3 = offsetY2 + colWidth * 1.5

                prop.colNum = Math.floor(width / (colWidth / 2))
                drawRoadMap(prop, bigs, RoadType.BIG, offsetY1, 0)

                prop.colNum = prop.colNum * 2
                drawRoadMap(prop, bigeyes, RoadType.BIGEYE, offsetY2, 0)

                prop.colNum = Math.floor(prop.colNum / 2)
                drawRoadMap(prop, smalls, RoadType.SMALL, offsetY3, 0)

                const offsetX1 = prop.colNum * (prop.colWidth / 4)
                drawRoadMap(
                    prop,
                    cockroachs,
                    RoadType.COCKROACH,
                    offsetY3,
                    offsetX1
                )
            }
        }

        const drawOnlyBigMap = (data: RoadMapData, isPre = false) => {
            const canvas = mapCanvas.value as HTMLCanvasElement
            let prop: RoadMapProperties | undefined
            if (props.borderColor !== '')
                prop = drawSingleGridMap(canvas, props.isTransparent, {
                    stroke: props.borderColor
                })
            else prop = drawSingleGridMap(canvas, props.isTransparent)

            if (prop && data) {
                const { bigs } = data
                prop.isPre = isPre
                prop.colWidth = prop.colWidth * 2
                prop.colNum = Math.floor(prop.width / (prop.colWidth / 2))
                if (props.lineWidth !== 0)
                    prop.lineWidth = props.lineWidth * prop.dpi
                else prop.lineWidth = 1 * prop.dpi
                drawRoadMap(prop, bigs, RoadType.BIG, 0, 0)
            }
        }

        const drawBeadOnly = (data: RoadMapData, isPre = false) => {
            const canvas = mapCanvas.value as HTMLCanvasElement

            let prop: RoadMapProperties | undefined

            if (props.borderColor !== '')
                prop = drawSingleGridMap(canvas, props.isTransparent, {
                    stroke: props.borderColor
                })
            else
                prop = drawSingleGridMap(canvas, props.isTransparent, {
                    stroke: 'rgba(223,230,236,0.2)'
                })

            if (prop && data) {
                prop.isPre = isPre
                const { beads } = data

                let lineWidth = 1.5

                if (props.lineWidth !== 0) lineWidth = props.lineWidth

                prop.lineWidth = lineWidth
                prop.colNum = props.beadRowCount
                drawRoadMap(prop, beads, RoadType.BEAD, 0, 0, props.gameType, t)
            }
        }

        const drawNoBeadVertical = (data: RoadMapData, isPre = false) => {
            const canvas = mapCanvas.value as HTMLCanvasElement
            let prop: RoadMapProperties | undefined

            if (props.borderColor !== '')
                prop = drawRoadMapGrid3(canvas, props.isTransparent, {
                    stroke: props.borderColor
                })
            else
                prop = drawRoadMapGrid3(canvas, props.isTransparent, {
                    stroke: 'rgba(223,230,236,0.2)'
                })

            if (prop && data) {
                prop.isPre = isPre
                const { bigs, bigeyes, smalls, cockroachs } = data
                prop.colNum = Math.floor(prop.width / prop.colWidth)
                prop.colWidth = prop.colWidth * 2

                let lineWidth = 2

                if (props.lineWidth !== 0) lineWidth = props.lineWidth

                prop.lineWidth = lineWidth * prop.dpi

                drawRoadMap(prop, bigs, RoadType.BIG, 0, 0, props.gameType, t)

                prop.colNum = Math.floor(prop.colNum / 3) * 2

                const top = prop.colWidth * 3
                const smallX = prop.colNum * (prop.colWidth / 4)
                const cockroachsX = (prop.colNum * prop.colWidth) / 2

                prop.colNum = prop.colNum - 1

                drawRoadMap(
                    prop,
                    bigeyes,
                    RoadType.BIGEYE,
                    top,
                    0,
                    props.gameType,
                    t
                )

                drawRoadMap(
                    prop,
                    smalls,
                    RoadType.SMALL,
                    top,
                    smallX,
                    props.gameType,
                    t
                )

                prop.colNum = prop.colNum + 1

                prop.lineWidth = lineWidth * 1.2 * prop.dpi

                drawRoadMap(
                    prop,
                    cockroachs,
                    RoadType.COCKROACH,
                    top,
                    cockroachsX,
                    props.gameType,
                    t
                )
            }
        }
        const Predict = (color: number) => {
            if (!props.enablePrediction) return

            predictionType.value = color
            predictionCount.value = predictionBlink.value

            if (typeof predictionTimer.value !== 'number') {
                predictionTimer.value = setInterval(() => {
                    // pre
                    if (predictionCount.value % 2 !== 0) {
                        if (predictionType.value === 1 && nextRed.value) {
                            handlePrediction(nextRed.value)
                        } else if (
                            predictionType.value === 2 &&
                            nextBlue.value
                        ) {
                            handlePrediction(nextBlue.value)
                        }
                    } else {
                        if (roadMapData.value) {
                            if (predictionType.value === 1 && nextRed.value) {
                                handlePrediction(nextRed.value, true)
                            } else if (
                                predictionType.value === 2 &&
                                nextBlue.value
                            ) {
                                handlePrediction(nextBlue.value, true)
                            } else {
                                handlePrediction(roadMapData.value)
                            }
                        }
                    }

                    if (
                        predictionCount.value === 0 &&
                        typeof predictionTimer.value === 'number'
                    ) {
                        clearInterval(predictionTimer.value)
                        predictionTimer.value = false
                    }

                    predictionCount.value--
                }, 200)
            }
        }

        const handlePrediction = (data: RoadMapData, isPre = false) => {
            if (props.display === DISPLAY.default_horizontal)
                drawDefaultHorizontalMap(data, isPre)
            else if (props.display === DISPLAY.default_vertical) {
                drawDefaultVerticalMap(data, isPre)
            } else if (props.display === DISPLAY.big) {
                drawOnlyBigMap(data, isPre)
            } else if (props.display === DISPLAY.no_bead_vertical) {
                // this.drawNoBeadVertical(data, isPre)
                drawNoBeadVertical(data, isPre)
            } else if (props.display === DISPLAY.no_bead_horizontal) {
                // drawNoBeadHorizontal(data, isPre)
            } else if (props.display === DISPLAY.bead_only) {
                drawBeadOnly(data, isPre)
            } else if (props.display === DISPLAY.default_horizontal_v2) {
                drawDefaultHorizontalMapV2(data, isPre)
            }
        }

        onMounted(() => {
            // listen on page active
            emitter.on(EventBuss.PAGE_ON_VISIBLE, handleOnPageActive)
            emitter.on(EventBuss.WINDOW_RESIZED, handleOnWindowResize)

            readyToRender.value = true

            if (props.enableClicked) {
                mapCanvas.value.addEventListener('click', handleClickEvent)
            }
            drawMap()
        })

        onBeforeUnmount(() => {
            emitter.off(EventBuss.PAGE_ON_VISIBLE, handleOnPageActive)
            emitter.off(EventBuss.WINDOW_RESIZED, handleOnWindowResize)
            if (props.enableClicked) {
                mapCanvas.value.removeEventListener('click', handleClickEvent)
            }
        })

        const handleClickEvent = (e: MouseEvent) => {
            if (mapCanvas.value && beadsArray) {
                const ctx = (mapCanvas.value as HTMLCanvasElement).getContext(
                    '2d'
                )

                const dpi = window.devicePixelRatio

                beadsArray.forEach((beadsRef) => {
                    if (
                        ctx?.isPointInPath(
                            beadsRef.path2D,
                            e.offsetX * dpi,
                            e.offsetY * dpi
                        )
                    ) {
                        context.emit('road-result-preview', beadsRef.index)
                    }
                })
            }
        }

        const handleOnPageActive = () => {
            // draw map again
            drawMap()
        }

        const handleOnWindowResize = () => {
            drawMap()
        }

        const lang = computed((): string => store.getters['lang'])
        //#region Watchers

        watch(
            () => props.mapData,
            (value) => {
                let strValue = ''
                if (value) strValue = value
                roadMapData.value = parseRoadMap(strValue)

                nextRed.value = parseRoadMap(`${strValue}10#`)
                nextBlue.value = parseRoadMap(`${strValue}30#`)

                if (props.enablePrediction) {
                    context.emit(
                        'road-map-data',
                        roadMapData.value,
                        nextRed.value,
                        nextBlue.value
                    )
                } else {
                    context.emit('road-map-data', roadMapData.value)
                }

                if (
                    readyToRender.value &&
                    typeof predictionTimer.value !== 'number'
                ) {
                    drawMap()
                }
            },
            { immediate: true }
        )

        watch(
            () => props.display,
            (newVal, oldVal) => {
                if (oldVal !== newVal) {
                    drawMap()
                }
            }
        )

        watch(
            () => lang.value,
            () => {
                drawMap()
                context.emit('road-map-data', roadMapData.value)
            }
        )

        //#endregion
        return {
            mapCanvas,
            drawMap,
            Predict
        }
    }
})
